import { datadogRum } from '@datadog/browser-rum';
import { ErrorBoundary } from '@datadog/browser-rum-react';
import { MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import branch from 'branch-sdk';
import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import CacheBuster from 'react-cache-buster';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import packageInfo from '../package.json';
import CacheClearer from './CacheClearer';
import PhoneVerifyModal from './components/Auth/PhoneVerifyModal';
import { AppRatingProvider } from './context/AppRatingProvider';
import { NewsProvider } from './context/newsProvide';
import { ThemeProvider, useTheme } from './context/themeProvider';
import usePhoneVerification from './hooks/usePhoneVerification';
import { LoadingSpinnerScreen, ScreenLoader } from './molecules/AppLoader';
import MaintenanceModal from './molecules/Maintainence/MaintainenceModal';
import { useLocalStorageStore } from './stores/localStorageStore';
import LayoutWrapper from './utils/LayoutWrapper';
import { MixpanelEvent, sendPostHogEvent } from './utils/mixpanelUtil';

// Create a new ErrorFallback component for route-level errors
const RouteErrorBoundary = ({ children }) => (
  <ErrorBoundary
    fallback={({ error }) => (
      <div className='flex min-h-screen flex-col items-center justify-center p-4'>
        <h2 className='mb-4 text-xl font-bold'>Something went wrong</h2>
        <p className='mb-4 text-gray-600'>{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className='rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600'
        >
          Retry
        </button>
      </div>
    )}
  >
    {children}
  </ErrorBoundary>
);

// Create a loading component with better UX
const RouteLoadingFallback = () => <ScreenLoader />;

// Wrap lazy components with error boundary and better loading state
const withErrorBoundary = (Component) => (props) => (
  <RouteErrorBoundary>
    <Suspense fallback={<RouteLoadingFallback />}>
      <Component {...props} />
    </Suspense>
  </RouteErrorBoundary>
);

// Lazy load components with error boundary wrapper
const HomePage = withErrorBoundary(lazy(() => import('./components/HomePage')));
const AccountDetailsPage = withErrorBoundary(
  lazy(() => import('./components/AccountDetails'))
);
const AuthPage = withErrorBoundary(lazy(() => import('./components/Auth')));
const SignupPage = withErrorBoundary(lazy(() => import('./components/SignUp')));
const OnboardingPage = withErrorBoundary(
  lazy(() => import('./components/Onboarding'))
);
const DuelPage = withErrorBoundary(lazy(() => import('./components/Duel')));
const DuelState = withErrorBoundary(
  lazy(() => import('./components/Duel/DuelState'))
);
const ChatPage = withErrorBoundary(lazy(() => import('./components/Chat')));
const PyqPage = withErrorBoundary(lazy(() => import('./components/Pyqs')));
const LatestNews = withErrorBoundary(
  lazy(() => import('./components/News/latestNews'))
);
const NewsTabs = withErrorBoundary(
  lazy(() => import('./components/News/newsTabs'))
);
const ProfilePage = withErrorBoundary(
  lazy(() => import('./components/Profile'))
);
const NewsDetailPage = withErrorBoundary(
  lazy(() => import('./components/News/newsDetailPage'))
);
const NewsPyqPage = withErrorBoundary(
  lazy(() => import('./components/News/newsPyqPage'))
);
const AccountDetailsForm = withErrorBoundary(
  lazy(() => import('./components/SignUp/AccountDetailsForm'))
);
const ChatScreen = withErrorBoundary(
  lazy(() => import('./components/Chat/TutorChat/ChatScreen'))
);
const EditProfile = withErrorBoundary(
  lazy(() => import('./components/Profile/EditProfile'))
);
const PyqSearch = withErrorBoundary(
  lazy(() => import('./components/Pyqs/PyqSearch'))
);
const MatchGame = withErrorBoundary(
  lazy(() => import('./components/Duel/MatchGame/MatchGame'))
);
const Matching = withErrorBoundary(
  lazy(() => import('./components/Duel/MatchGame/Matching'))
);
const DuelStart = withErrorBoundary(
  lazy(() => import('./components/Duel/MatchGame/DuelStart'))
);
const DuelResults = withErrorBoundary(
  lazy(() => import('./components/Duel/MatchGame/DuelResults'))
);
const DuelReview = withErrorBoundary(
  lazy(() => import('./components/Duel/DuelReview'))
);
const NewsMcqPage = withErrorBoundary(
  lazy(() => import('./components/News/newsMCQpage'))
);
const McqDiscussAi = withErrorBoundary(
  lazy(() => import('./components/Chat/TutorChat/McqDiscussAI'))
);
const SelectBook = withErrorBoundary(
  lazy(() => import('./components/Chat/AskABook/SelectBook'))
);
const BookChat = withErrorBoundary(
  lazy(() => import('./components/Chat/AskABook/BookChat'))
);
const DuelFriendsList = withErrorBoundary(
  lazy(() => import('./components/Duel/DuelFriendsList'))
);
const CustomizePractice = withErrorBoundary(
  lazy(() => import('./components/Practice/CustomizePractice'))
);
const PracticeQuestions = withErrorBoundary(
  lazy(() => import('./components/Practice/PracticeQuestions'))
);
const TabsComp = withErrorBoundary(
  lazy(() => import('./components/Practice/TabsComp'))
);
const NewTest = withErrorBoundary(
  lazy(() => import('./components/Practice/MockTest/NewTest'))
);
const OngoingTest = withErrorBoundary(
  lazy(() => import('./components/Practice/MockTest/OngoingTest'))
);
const TestResult = withErrorBoundary(
  lazy(() => import('./components/Practice/MockTestResults'))
);
const ReviewAnswer = withErrorBoundary(
  lazy(() => import('./components/Practice/MockTestResults/ReviewAnswere'))
);
const ChangeTheme = withErrorBoundary(
  lazy(() => import('./molecules/ChangeTheme'))
);
const MockTestInsights = withErrorBoundary(
  lazy(() => import('./components/Practice/MockTestInsights'))
);

const queryClient = new QueryClient();

// Create a screen tracker component
const ScreenTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Get the current pathname as screen name
    const screenName = location.pathname.substring(1) || 'home';
    const formattedScreenName = screenName
      .replace(/\//g, '-')
      .replace(/^-/, '');

    // Start tracking when the route changes
    const startTime = new Date().getTime();

    // Define any extra parameters you want to track
    const extraParams = {
      path: location.pathname,
      referrer: document.referrer,
      // Add any state params if relevant
      ...(location.state || {}),
    };

    // Track when component unmounts or route changes
    return () => {
      const endTime = new Date().getTime();
      const elapsedTime = endTime - startTime;

      const formatTime = (ms) => {
        const seconds = Math.floor(ms / 1000);
        return `${seconds} seconds`;
      };

      // Track the event
      MixpanelEvent(`Screen View - ${formattedScreenName}`, {
        screenTime: formatTime(elapsedTime),
        ...extraParams,
      });

      // If you're using PostHog as well
      sendPostHogEvent(`Screen View - ${formattedScreenName}`, {
        screenTime: formatTime(elapsedTime),
        ...extraParams,
      });

      MixpanelEvent(`screen_name`, {
        screen_time: formatTime(elapsedTime),
        screen: formattedScreenName,
        ...extraParams,
      });

      // If you're using PostHog as well
      sendPostHogEvent(`screen_name`, {
        screen_time: formatTime(elapsedTime),
        screen: formattedScreenName,
        ...extraParams,
      });
    };
  }, [location]);

  return null;
};

const AppContent = React.memo(() => {
  // Read userData from the store using the hook for reactivity
  const storeUserData = useLocalStorageStore((state) => state.userData);
  const storeIsSignupNewUser = useLocalStorageStore(
    (state) => state.isSignupNewUser
  );

  const {
    firstOpen: storeFirstOpen,
    branchData: storeBranchData,
    intendedDestination: storeIntendedDestination,
    setFirstOpen,
    setIntendedDestination,
    clearIntendedDestination,
    setBranchData,
    clearBranchData,
  } = useLocalStorageStore((state) => ({
    firstOpen: state.firstOpen,
    branchData: state.branchData,
    intendedDestination: state.intendedDestination,
    setFirstOpen: state.setFirstOpen,
    setIntendedDestination: state.setIntendedDestination,
    clearIntendedDestination: state.clearIntendedDestination,
    setBranchData: state.setBranchData,
    clearBranchData: state.clearBranchData,
  }));

  const navigate = useNavigate();
  const location = useLocation();
  const { colorScheme } = useTheme();

  // Derive isLoggedIn directly from storeUserData
  const isLoggedIn = !!(storeUserData && Object.keys(storeUserData).length > 0);
  const [deepLinkProcessed, setDeepLinkProcessed] = useState(false);

  const { showPhoneVerifyModal, setShowPhoneVerifyModal, handleVerifyNow } =
    usePhoneVerification(isLoggedIn); // isLoggedIn is now reactive

  useEffect(() => {
    const appOpened = () => {
      const isFirstOpen = useLocalStorageStore.getState().firstOpen !== 'done';
      MixpanelEvent(
        'app_opened',
        { type: isFirstOpen ? 'new' : 'returning' },
        true
      );
      sendPostHogEvent(
        'app_opened',
        { type: isFirstOpen ? 'new' : 'returning' },
        true
      );
    };
    appOpened();

    return () => {
      MixpanelEvent('app_closed', {}, true);
      sendPostHogEvent('app_closed', {}, true);
      setFirstOpen('done');
    };
  }, []);

  useEffect(() => {
    try {
      branch.data((err, data) => {
        // console.log('branch data', data);

        if (err) {
          console.error('Error getting deep link data:', err);
          if (!sessionStorage.getItem('hasReloaded')) {
            sessionStorage.setItem('hasReloaded', 'true');
            window.location.reload();
          }
          return;
        }

        if (data === null || data === undefined) {
          window.location.reload();
          return;
        }

        const linkData = data.data_parsed;

        if (
          !deepLinkProcessed &&
          linkData['+clicked_branch_link'] &&
          linkData?.dl_event_route
        ) {
          setDeepLinkProcessed(true);

          const path = linkData.dl_event_route;
          const state = Object.keys(linkData).reduce((acc, key) => {
            if (key.startsWith('dl_data_')) {
              acc[key.replace('dl_data_', 'dl_')] = linkData[key];
            }
            return acc;
          }, {});
          const isPreviewLink = Boolean(linkData?.is_preview_link === 'true');

          if (isLoggedIn) {
            navigate(path, { state });
          } else {
            if (isPreviewLink) {
              navigate(path, { state });
            } else {
              setIntendedDestination({ path, state });
              setBranchData(linkData);
              navigate('/login');
            }
          }
        }
      });
    } catch (error) {
      console.error('Error getting deep link data in useEffect:', error);
      window.location.reload();
    }
  }, [
    navigate,
    deepLinkProcessed,
    isLoggedIn,
    setIntendedDestination,
    setBranchData,
    setFirstOpen,
  ]);

  useEffect(() => {
    const currentIsSignupNewUser =
      useLocalStorageStore.getState().isSignupNewUser;
    if (currentIsSignupNewUser === 'true') {
      navigate('/signupdetails');
      const currentBranchData = useLocalStorageStore.getState().branchData;
      if (currentBranchData && currentBranchData.link_type === 'pwa_install') {
        const currentMixpanelUserData =
          useLocalStorageStore.getState().userData || {};
        MixpanelEvent(
          'Signup via Deeplink',
          {
            deepLinkData: currentBranchData,
            link_type: currentBranchData.link_type || 'branch',
            user_id: currentMixpanelUserData.user,
            name: currentMixpanelUserData.name,
            phone: currentMixpanelUserData.phone,
          },
          true
        );
        sendPostHogEvent(
          'Signup via Deeplink',
          {
            deepLinkData: currentBranchData,
            link_type: currentBranchData.link_type || 'branch',
            user_id: currentMixpanelUserData.user,
            name: currentMixpanelUserData.name,
            phone: currentMixpanelUserData.phone,
          },
          true
        );
        clearBranchData();
      }
    } else if (isLoggedIn) {
      const currentIntendedDestination =
        useLocalStorageStore.getState().intendedDestination;
      if (currentIntendedDestination && currentIntendedDestination.path) {
        const { path, state } = currentIntendedDestination;
        if (path) {
          navigate(path, { state });
          setTimeout(() => {
            clearIntendedDestination();
          }, 2000);
        }
      }
    }
  }, [isLoggedIn, navigate, clearBranchData, clearIntendedDestination]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      datadogRum?.startView(location.pathname);
    }
  }, [navigate, location.pathname]);

  const PrivateRoute = useCallback(
    ({ element, redirectPath = '/login' }) => {
      if (!isLoggedIn) {
        return (
          <Navigate
            to={redirectPath}
            state={{ from: window.location.pathname }}
            replace
          />
        );
      }

      // Ensure element is a valid React element
      if (!element) {
        console.error('PrivateRoute: element prop is undefined');
        return null;
      }

      return element;
    },
    [isLoggedIn]
  );

  const Loader = () => {
    return <LoadingSpinnerScreen />;
  };

  return (
    <MantineProvider withGlobalStyles withNormalizeCSS theme={{ colorScheme }}>
      <CacheClearer />
      <LayoutWrapper>
        {/* Global Phone Verify Modal */}
        <PhoneVerifyModal
          opened={showPhoneVerifyModal}
          onClose={() => setShowPhoneVerifyModal(false)}
          onVerifyNow={handleVerifyNow}
        />
        <MaintenanceModal />
        <ScreenTracker />
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path='/' element={<HomePage />} />
            <Route
              path='/login'
              element={
                isLoggedIn ? (
                  <Navigate to={location.state?.from || '/news'} replace />
                ) : (
                  <AuthPage />
                )
              }
            />
            <Route
              path='/profile'
              element={<PrivateRoute element={<ProfilePage />} />}
            />
            <Route
              path='/edit-profile'
              element={<PrivateRoute element={<EditProfile />} />}
            />
            <Route
              path='/signup'
              element={
                isLoggedIn ? (
                  <Navigate to={location.state?.from || '/news'} replace />
                ) : (
                  <SignupPage />
                )
              }
            />
            <Route
              path='/signupdetails'
              element={
                isLoggedIn && !(storeIsSignupNewUser === 'true') ? (
                  <Navigate to={location.state?.from || '/news'} replace />
                ) : (
                  <AccountDetailsForm />
                )
              }
            />
            <Route
              path='/accountDetails'
              element={<PrivateRoute element={<AccountDetailsPage />} />}
            />
            <Route
              path='/onboarding'
              element={
                isLoggedIn ? (
                  <Navigate to={location.state?.from || '/news'} replace />
                ) : (
                  <OnboardingPage />
                )
              }
            />
            <Route
              path='/news'
              element={<PrivateRoute element={<NewsTabs />} />}
            />
            <Route
              path='/news-list'
              element={
                !isLoggedIn ? (
                  <LatestNews isPublicNewsList={true} />
                ) : (
                  <Navigate to='/news' />
                )
              }
            />
            <Route path='/news/:id' element={<NewsDetailPage />} />
            <Route
              path='/news/pyq/:id'
              element={<PrivateRoute element={<NewsPyqPage />} />}
            />
            <Route
              path='/news/mcq/:id'
              element={<PrivateRoute element={<NewsMcqPage />} />}
            />
            <Route
              path='/duel'
              element={<PrivateRoute element={<DuelPage />} />}
            />
            <Route path='/duelState' element={<DuelState />} />
            <Route
              path='/chat'
              element={<PrivateRoute element={<ChatPage />} />}
            />
            <Route path='/chat/mcq/' element={<McqDiscussAi />} />
            <Route
              path='/chat/new/'
              element={<PrivateRoute element={<ChatScreen />} />}
            />
            <Route path='/chat/:id' element={<ChatScreen />} />
            <Route
              path='/pyq'
              element={<PrivateRoute element={<PyqPage />} />}
            />
            <Route
              path='/pyqSearch'
              element={<PrivateRoute element={<PyqSearch />} />}
            />
            <Route
              path='/revision'
              element={<PrivateRoute element={<TabsComp />} />}
            />
            <Route
              path='/online-match-game'
              element={<PrivateRoute element={<MatchGame />} />}
            />
            <Route path='/matching' element={<Matching />} />
            <Route path='/duel-start' element={<DuelStart />} />
            <Route path='/duel-results' element={<DuelResults />} />
            <Route path='/duel-review' element={<DuelReview />} />
            <Route
              path='/book-chat/select-book'
              element={<PrivateRoute element={<SelectBook />} />}
            />
            <Route
              path='/book/:id'
              element={<PrivateRoute element={<BookChat />} />}
            />
            <Route path='/duel-friends' element={<DuelFriendsList />} />
            <Route
              path='/customize-practice'
              element={<PrivateRoute element={<CustomizePractice />} />}
            />
            <Route path='/practice-questions' element={<PracticeQuestions />} />
            <Route path='/review' element={<DuelReview />} />
            <Route path='/new-test' element={<NewTest />} />
            <Route path='/mock-test' element={<OngoingTest />} />
            <Route path='/test-result' element={<TestResult />} />
            <Route path='/test-review' element={<ReviewAnswer />} />
            <Route path='/change-theme' element={<ChangeTheme />} />
            <Route path='/mock-test-insights' element={<MockTestInsights />} />
            <Route
              path='/verify-phone'
              element={<AuthPage isVerifyPhone={true} />}
            />
            <Route path='*' element={<Navigate to='/' />} />
          </Routes>
        </Suspense>
      </LayoutWrapper>
    </MantineProvider>
  );
});

const App = () => {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        // Log to DataDog
        datadogRum.addError(error, {
          errorInfo,
          source: 'app_root',
        });
      }}
      fallback={({ error }) => (
        <div className='flex min-h-screen flex-col items-center justify-center p-4'>
          <h1 className='mb-4 text-2xl font-bold'>
            Oops! Something went wrong
          </h1>
          <p className='mb-4 text-gray-600'>{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className='rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600'
          >
            Reload App
          </button>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <NewsProvider>
            <AppRatingProvider>
              <MantineProvider>
                <CacheBuster
                  currentVersion={packageInfo.version}
                  isEnabled={true}
                  isVerboseMode={false}
                >
                  <Router>
                    <CacheClearer />
                    <AppContent />
                  </Router>
                </CacheBuster>
              </MantineProvider>
            </AppRatingProvider>
          </NewsProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
